import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";
import { Loader2 } from "lucide-react"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, onClick, children, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  const lockRef = React.useRef(false)
  const [locked, setLocked] = React.useState(false)
  const handleClick = (e) => {
    if (lockRef.current) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    const result = onClick(e)
    if (result && typeof result.then === "function") {
      lockRef.current = true
      setLocked(true)
      const unlock = () => { lockRef.current = false; setLocked(false) }
      result.then(unlock, unlock)
    }
  }
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }), locked && !asChild && "qa-btn-locked")}
      ref={ref}
      {...props}
      onClick={onClick ? handleClick : undefined}
      disabled={asChild ? props.disabled : (props.disabled || locked)}>
      {asChild ? children : (<>{locked && <Loader2 className="animate-spin" aria-hidden="true" />}{children}</>)}
    </Comp>
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
